// Every request to GitHub (and to the changelist server on GitHub's behalf)
// goes through one Scheduler. A request says what it is worth (`priority`),
// what it costs (`resource`, `cost`) and whether sending it twice is safe;
// the scheduler decides when it is sent:
//
// - concurrency: at most `concurrency` requests in flight, plus
//   `interactiveReserve` slots only interactive requests may take, so a
//   click never waits behind a full queue of background reads;
// - order: by priority, then first come first served; a request moves up
//   one class for every `ageMs` it has waited, so none starves, but never
//   above `visible`, so waiting background work never goes ahead of a click;
// - dedupe: requests with the same `key` waiting or in flight share one
//   send (raised to the most urgent caller's priority); writes never do;
// - budget: GitHub's hourly budget per resource (`graphql`, `core`,
//   `search`), as answers report it and as this client spends it. A request
//   that would overdraw it is refused unsent; smaller ones that fit still go.
//   Every 30 s one interactive request is let through anyway (a probe), so
//   a stale budget corrects itself;
// - rate limits: a primary limit pauses its resource (probed like a spent
//   budget); a secondary limit, or Changeling's own, pauses every resource.
//   A secondary limit GitHub gave no wait for pauses twice as long each
//   time in a row, and after one the scheduler sends one request at a
//   time, widening by one with each success. A request may be held through
//   a pause that ends within its `holdUpTo` instead of being refused (the
//   user's writes); `onHeld` says until when;
// - retries: a failed idempotent request is sent again as often as
//   `retries` says for its error, waiting `backoffMs` between tries
//   (without holding a slot); `onRetrying` says when the next one goes;
// - cancellation: a caller's `AbortSignal` drops its request while it
//   waits and aborts it in flight, once no other caller shares it.
//
// `run` never throws: it answers a `Result`, whose failure says which of
// those happened. `orThrow` turns that back into the errors callers of the
// throwing API (`graphql`, `rest`, …) catch.
import { backoffMs } from '../logic/backoff';
import { RateLimitError, type Budget, type RateLimit } from './rateLimit';

/**
 * What a request is worth, most urgent first:
 * - `interactive`: the user is waiting on this answer now (their write, the file they opened);
 * - `visible`: it fills what is on screen;
 * - `background`: it keeps what is on screen current, unasked (check refreshes, annotations);
 * - `prefetch`: it may be wanted later (files not yet opened).
 */
export type Priority = 'interactive' | 'visible' | 'background' | 'prefetch';

/** Each priority's class, the most urgent lowest. */
export const RANK: Record<Priority, number> = { interactive: 0, visible: 1, background: 2, prefetch: 3 };

/**
 * What a request spends: one of GitHub's hourly budgets (`graphql` points,
 * `core` REST requests, `search` REST searches), or none of them
 * (`changelist`: the changelist server's own endpoints, bound only by its
 * per-user rate and the shared concurrency).
 */
export type Resource = 'graphql' | 'core' | 'search' | 'changelist';

export interface Request<T> {
  priority: Priority;
  resource: Resource;
  /** Estimated cost against the resource's budget: GraphQL points, or 1 per REST request (the default). */
  cost?: number;
  /** Requests with the same key waiting or in flight share one send. Only for reads. */
  key?: string;
  /** Aborting drops the request while it waits and aborts it in flight. */
  signal?: AbortSignal;
  /** Whether sending it again is safe (default true). Writes say false: they are never retried or merged. */
  idempotent?: boolean;
  /**
   * Hold it through a pause that ends within this many ms of the request,
   * and send it then, instead of refusing it at once (default 0).
   */
  holdUpTo?: number;
  /** Sends the request once. `signal` aborts when no caller wants the answer any more. */
  send: (signal: AbortSignal) => Promise<T>;
}

/** Why a request has no answer. */
export type RequestError =
  /** A caller's signal aborted (or `cancelAll`), with its reason. */
  | { kind: 'aborted'; reason: unknown }
  /** A rate limit refused it, sent or not; calls to its resource resume at `limit.until`. */
  | { kind: 'limited'; limit: RateLimit }
  /**
   * It failed: `error` is the last failure, after `attempts` tries.
   * `retryable`: the failure may pass on its own (the retry policy allows
   * tries for it), so trying again later may work; the automatic tries
   * are spent either way.
   */
  | { kind: 'failed'; error: Error; attempts: number; retryable: boolean };

export type Result<T> = { ok: true; value: T } | { ok: false; error: RequestError };

/**
 * The value, or the error a caller of a throwing API expects: an abort's
 * reason (an `AbortError` when the reason is no Error), a `RateLimitError`,
 * or the failure itself.
 */
export function orThrow<T>(r: Result<T>): T {
  if (r.ok) return r.value;
  const e = r.error;
  switch (e.kind) {
    case 'aborted':
      throw e.reason instanceof Error ? e.reason : new DOMException(String(e.reason ?? 'aborted'), 'AbortError');
    case 'limited':
      throw new RateLimitError(e.limit);
    case 'failed':
      throw e.error;
  }
}

export interface SchedulerOptions {
  /** Requests in flight at most, besides the interactive reserve. */
  concurrency: number;
  /** Further slots that only interactive requests take. */
  interactiveReserve: number;
  /** A waiting request moves up one priority class for every `ageMs` it has waited. */
  ageMs: number;
  /** How many times a failed idempotent request is tried again, by its error (0: not again). */
  retries: (error: Error) => number;
  /** Jitter source for retry waits and resumes. */
  random?: () => number;
  /** How often an interactive request may probe a spent budget or a primary pause (default 30 s). */
  probeMs?: number;
}

/** What `snapshot` keeps of the limits in force, to `restore` in a new scheduler (JSON-safe). */
export interface LimitsSnapshot {
  pauses: [PauseScope, RateLimit][];
  strikes: number;
}

/** Failed requests waiting to be sent again: when the next one goes (epoch ms), and which try of it that is (2: the first retry). */
export interface Retrying {
  at: number;
  attempt: number;
}

/** The longest a secondary limit GitHub gave no wait for pauses. */
const MAX_SECONDARY_MS = 15 * 60_000;

interface Waiter {
  resolve: (r: Result<unknown>) => void;
  signal?: AbortSignal;
  onAbort?: () => void;
  /** The caller whose answer is the original; the others get copies. */
  first: boolean;
}

interface Job {
  seq: number;
  /** When it joined the queue, for aging. */
  since: number;
  rank: number;
  resource: Resource;
  cost: number;
  key: string | undefined;
  idempotent: boolean;
  send: (signal: AbortSignal) => Promise<unknown>;
  waiters: Set<Waiter>;
  controller: AbortController;
  failures: number;
  state: 'queued' | 'retrying' | 'held' | 'running' | 'done';
  timer: ReturnType<typeof setTimeout> | null;
  /** Held through pauses ending by this time (epoch ms); 0: refused at once. */
  holdUntil: number;
  /** While held: when it is sent. */
  heldTill: number | null;
  /** While retrying: when it is sent again. */
  retryAt: number | null;
}

/** Pauses are per resource; secondary and Changeling's own limits pause `all`. */
export type PauseScope = Resource | 'all';

/** Two budget reports whose resets lie this close are of one window (GitHub's are an hour apart). */
const SAME_WINDOW_MS = 5 * 60_000;

export class Scheduler {
  private seq = 0;
  private queue: Job[] = [];
  private running = 0;
  /** Requests in flight allowed now: `concurrency`, narrowed after a secondary limit. */
  private window: number;
  private byKey = new Map<string, Job>();
  private pauses = new Map<PauseScope, RateLimit>();
  /** The latest budget answers reported per resource, and what this client has spent since. */
  private budgets = new Map<string, { reported: Budget; spent: number }>();
  /** Secondary limits in a row without a stated wait (reset by a success). */
  private strikes = 0;
  private random: () => number;

  /** When GitHub last said something of a resource's limit, or a probe was sent: probes wait `probeMs` from it. */
  private lastWord = new Map<Resource, number>();
  /** Until when requests were refused on a resource's spent budget (for `afterPause`). */
  private spentUntil = new Map<Resource, number>();
  private heldListeners = new Set<(until: number | null) => void>();
  private heldShown: number | null = null;
  private limitListeners = new Set<(s: LimitsSnapshot) => void>();
  private retryListeners = new Set<(r: Retrying | null) => void>();
  private retryShown: Retrying | null = null;

  constructor(private opts: SchedulerOptions) {
    this.window = opts.concurrency;
    this.random = opts.random ?? Math.random;
  }

  /** Schedule `req`; never rejects. */
  run<T>(req: Request<T>): Promise<Result<T>> {
    return new Promise<Result<T>>((resolve) => {
      const signal = req.signal;
      if (signal?.aborted) return resolve({ ok: false, error: { kind: 'aborted', reason: signal.reason } });
      const idempotent = req.idempotent ?? true;
      const key = idempotent ? req.key : undefined;
      const shared = key === undefined ? undefined : this.byKey.get(key);
      const waiter: Waiter = { resolve: resolve as (r: Result<unknown>) => void, signal, first: !shared };
      if (shared) {
        shared.rank = Math.min(shared.rank, RANK[req.priority]);
        this.attach(shared, waiter);
        this.pump();
        return;
      }
      const job: Job = {
        seq: ++this.seq,
        since: Date.now(),
        rank: RANK[req.priority],
        resource: req.resource,
        cost: Math.max(0, req.cost ?? 1),
        key,
        idempotent,
        send: req.send,
        waiters: new Set(),
        controller: new AbortController(),
        failures: 0,
        state: 'queued',
        timer: null,
        holdUntil: req.holdUpTo ? Date.now() + req.holdUpTo : 0,
        heldTill: null,
        retryAt: null,
      };
      this.attach(job, waiter);
      if (key !== undefined) this.byKey.set(key, job);
      this.queue.push(job);
      this.pump();
    });
  }

  /**
   * Note the hourly budget a GitHub answer reported (its `resetAt` on this
   * device's clock). Answers to requests in flight together arrive in any
   * order: within one reset window (resets within SAME_WINDOW_MS, as clock
   * offsets move them) a report with no less left than the one kept says
   * nothing new, so it is ignored, and what was sent since is kept.
   */
  noteBudget(b: Budget | null): void {
    if (!b?.resource) return;
    const kept = this.budgets.get(b.resource)?.reported;
    if (kept && Math.abs(kept.resetAt - b.resetAt) < SAME_WINDOW_MS && b.remaining >= kept.remaining) return;
    this.budgets.set(b.resource, { reported: b, spent: 0 });
    if (isBudgeted(b.resource)) {
      this.lastWord.set(b.resource, Date.now());
      this.spentUntil.delete(b.resource);
      // A budget with points left ends the resource's primary pause, and frees what was held through it.
      const paused = this.pauses.get(b.resource);
      if (b.remaining > 0 && paused?.kind === 'primary') {
        this.pauses.delete(b.resource);
        this.limitsChanged();
        this.releaseHeld();
      }
    }
  }

  /** Queue every held request again now; those still blocked are held again (a pause ended early). */
  private releaseHeld() {
    let any = false;
    for (const job of this.active) {
      if (job.state !== 'held') continue;
      if (job.timer !== null) clearTimeout(job.timer);
      job.timer = null;
      job.state = 'queued';
      job.heldTill = null;
      this.active.delete(job);
      this.queue.push(job);
      any = true;
    }
    if (any) this.pump();
  }

  /** Pause for a limit an answer reported (see `failed`), e.g. one the server met on the client's behalf. */
  noteLimit(l: RateLimit, resource: Resource): RateLimit {
    const kept = this.limitFrom(l, resource);
    this.pump();
    return kept;
  }

  /** The latest budget an answer reported for `resource`, while still current; null otherwise. */
  budget(resource: string): Budget | null {
    const b = this.budgets.get(resource)?.reported;
    return b && b.resetAt > Date.now() ? b : null;
  }

  /** What is left of `resource`'s budget by this client's own count: as reported, less what was sent since. */
  left(resource: string): number | null {
    const b = this.budget(resource);
    return b ? b.remaining - this.budgets.get(resource)!.spent : null;
  }

  /**
   * Pause calls to `scope` until `limit.until` (a later pause in force is
   * kept). Waiting requests to it are refused as they come up.
   */
  pause(limit: RateLimit, scope: PauseScope): RateLimit {
    const kept = this.hold(limit, scope);
    this.pump();
    return kept;
  }

  /** `pause` without sending or refusing anything yet. */
  private hold(limit: RateLimit, scope: PauseScope): RateLimit {
    const now = Date.now();
    const held = this.pauses.get(scope);
    const kept = held && held.until > now && held.until > limit.until ? held : limit;
    this.pauses.set(scope, kept);
    if (scope !== 'all') this.lastWord.set(scope, now);
    this.limitsChanged();
    return kept;
  }

  /** The limit in force for `resource` (its own or every resource's), the later one; null when none. */
  private pausedFor(resource: Resource): RateLimit | null {
    const now = Date.now();
    let out: RateLimit | null = null;
    for (const scope of [resource, 'all'] as const) {
      const l = this.pauses.get(scope);
      if (l && l.until > now && (!out || l.until > out.until)) out = l;
    }
    return out;
  }

  /** The limit in force with the latest end, over every resource; null when calls are not paused. */
  limitState(): RateLimit | null {
    const now = Date.now();
    let out: RateLimit | null = null;
    for (const l of this.pauses.values()) if (l.until > now && (!out || l.until > out.until)) out = l;
    return out;
  }

  /** Epoch ms until which some calls are paused, or null. */
  limitedUntil(): number | null {
    return this.limitState()?.until ?? null;
  }

  /**
   * Run `fn` once no pause is in force, plus up to `jitter` ms so tabs
   * that waited together do not all resume at once; at once (next turn)
   * when none is. A pause extended meanwhile is waited out too. Returns a
   * cancel function.
   */
  afterPause(fn: () => void, jitter = 0): () => void {
    let timer: ReturnType<typeof setTimeout>;
    const arm = () => {
      const until = this.resumeAt();
      const wait = until === null ? 0 : until - Date.now() + this.random() * jitter;
      timer = setTimeout(() => (this.resumeAt() === null ? fn() : arm()), Math.max(0, wait));
    };
    arm();
    return () => clearTimeout(timer);
  }

  /** When refused work may go again: the latest end of a pause or of a spent budget that refused something; null when now. */
  private resumeAt(): number | null {
    const now = Date.now();
    let out = this.limitedUntil();
    for (const [resource, until] of this.spentUntil) {
      if (until <= now || !this.budget(resource)) this.spentUntil.delete(resource);
      else if (out === null || until > out) out = until;
    }
    return out;
  }

  /**
   * Calls `fn` with when the requests held through a pause are sent (the
   * latest), and with null once none is held. Returns the unsubscribe.
   */
  onHeld(fn: (until: number | null) => void): () => void {
    this.heldListeners.add(fn);
    return () => this.heldListeners.delete(fn);
  }

  /**
   * Calls `fn` with the next retry of the failed requests waiting to be
   * sent again (the soonest), and with null once none waits. Returns the
   * unsubscribe.
   */
  onRetrying(fn: (r: Retrying | null) => void): () => void {
    this.retryListeners.add(fn);
    return () => this.retryListeners.delete(fn);
  }

  private retryingChanged() {
    let next: Retrying | null = null;
    for (const job of this.active)
      if (job.state === 'retrying' && job.retryAt !== null && (next === null || job.retryAt < next.at)) next = { at: job.retryAt, attempt: job.failures + 1 };
    const shown = this.retryShown;
    if (next === shown || (next && shown && next.at === shown.at && next.attempt === shown.attempt)) return;
    this.retryShown = next;
    for (const fn of this.retryListeners) fn(next);
  }

  /** Calls `fn` whenever the pauses or the secondary-limit streak change. Returns the unsubscribe. */
  onLimits(fn: (s: LimitsSnapshot) => void): () => void {
    this.limitListeners.add(fn);
    return () => this.limitListeners.delete(fn);
  }

  /** The pauses in force and the secondary-limit streak. */
  snapshot(): LimitsSnapshot {
    const now = Date.now();
    return { pauses: [...this.pauses].filter(([, l]) => l.until > now), strikes: this.strikes };
  }

  /** Take on the pauses and streak a `snapshot` kept (a later pause in force here is kept). */
  restore(s: LimitsSnapshot): void {
    for (const [scope, l] of s.pauses) this.hold(l, scope);
    this.strikes = Math.max(this.strikes, s.strikes);
    if (s.strikes) this.window = Math.min(this.window, 1);
    this.pump();
  }

  private limitsChanged() {
    if (!this.limitListeners.size) return;
    const snap = this.snapshot();
    for (const fn of this.limitListeners) fn(snap);
  }

  private heldChanged() {
    let until: number | null = null;
    for (const job of this.active) if (job.state === 'held' && job.heldTill !== null && (until === null || job.heldTill > until)) until = job.heldTill;
    if (until === this.heldShown) return;
    this.heldShown = until;
    for (const fn of this.heldListeners) fn(until);
  }

  /** Abort every request waiting or in flight with `reason`, and forget every pause and budget. */
  cancelAll(reason: unknown): void {
    const jobs = new Set([...this.queue, ...this.byKey.values()]);
    for (const job of this.active) jobs.add(job);
    for (const job of jobs) this.finish(job, { ok: false, error: { kind: 'aborted', reason } });
    this.queue = [];
    this.reset();
    this.heldChanged();
  }

  /** Forget every pause and budget (tests; a new signed-in account). */
  reset(): void {
    this.pauses.clear();
    this.budgets.clear();
    this.lastWord.clear();
    this.spentUntil.clear();
    this.strikes = 0;
    this.window = this.opts.concurrency;
    this.limitsChanged();
    this.releaseHeld();
    this.heldChanged();
  }

  /** Requests in flight and waiting to be sent (retry waits included). */
  stats(): { running: number; queued: number } {
    return { running: this.running, queued: this.queue.length };
  }

  /** Jobs sent or waiting to retry: kept so `cancelAll` reaches them. */
  private active = new Set<Job>();

  private attach(job: Job, w: Waiter) {
    job.waiters.add(w);
    if (!w.signal) return;
    w.onAbort = () => {
      job.waiters.delete(w);
      w.resolve({ ok: false, error: { kind: 'aborted', reason: w.signal!.reason } });
      if (!job.waiters.size) this.drop(job, w.signal!.reason);
    };
    w.signal.addEventListener('abort', w.onAbort, { once: true });
  }

  /** No caller wants `job` any more: unqueue it, or abort it in flight. */
  private drop(job: Job, reason: unknown) {
    if (job.state === 'done') return;
    job.controller.abort(reason);
    if (job.timer !== null) clearTimeout(job.timer);
    job.timer = null;
    this.queue = this.queue.filter((j) => j !== job);
    this.forget(job);
    // A job in flight keeps its slot until its send settles (the abort ends it).
    if (job.state !== 'running') {
      job.state = 'done';
      this.active.delete(job);
    }
    this.pump();
    this.heldChanged();
    this.retryingChanged();
  }

  private forget(job: Job) {
    if (job.key !== undefined && this.byKey.get(job.key) === job) this.byKey.delete(job.key);
  }

  /**
   * Answer every caller of `job` (copies for all but the first) and retire
   * it. A job in flight answered early (`cancelAll`) is aborted, and keeps
   * its slot until its send settles.
   */
  private finish(job: Job, r: Result<unknown>) {
    if (job.timer !== null) clearTimeout(job.timer);
    job.timer = null;
    this.forget(job);
    if (job.state === 'running') {
      if (!r.ok && r.error.kind === 'aborted') job.controller.abort(r.error.reason);
    } else {
      job.state = 'done';
      this.active.delete(job);
    }
    for (const w of job.waiters) {
      if (w.onAbort) w.signal!.removeEventListener('abort', w.onAbort);
      w.resolve(r.ok && !w.first ? { ok: true, value: copy(r.value) } : r);
    }
    job.waiters.clear();
    this.retryingChanged();
  }

  /** The class a waiting job counts as now: its own, raised one per `ageMs` waited, but never above `visible`. */
  private effectiveRank(job: Job, now: number): number {
    return Math.max(Math.min(job.rank, RANK.visible), job.rank - Math.floor((now - job.since) / this.opts.ageMs));
  }

  private canStart(job: Job): boolean {
    if (this.running < this.window) return true;
    return job.rank === RANK.interactive && this.running < this.window + this.opts.interactiveReserve;
  }

  private pumping = false;
  private pumpAgain = false;

  /**
   * Send what may be sent now; refuse what a pause or the budget forbids.
   * A pump asked for while one runs (a send that settles at once, a pause)
   * runs again after it, never inside it.
   */
  private pump() {
    if (this.pumping) {
      this.pumpAgain = true;
      return;
    }
    this.pumping = true;
    try {
      do {
        this.pumpAgain = false;
        this.pumpOnce();
      } while (this.pumpAgain);
    } finally {
      this.pumping = false;
    }
  }

  private pumpOnce() {
    const now = Date.now();
    this.queue.sort((a, b) => this.effectiveRank(a, now) - this.effectiveRank(b, now) || a.seq - b.seq);
    for (let i = 0; i < this.queue.length; ) {
      const job = this.queue[i];
      const paused = this.pausedFor(job.resource);
      const limited = paused ?? this.overdraws(job);
      const probe = limited !== null && limited.kind === 'primary' && this.mayProbe(job, now);
      if (limited && !probe) {
        this.queue.splice(i, 1);
        if (!paused) this.spentUntil.set(job.resource, Math.max(this.spentUntil.get(job.resource) ?? 0, limited.until));
        if (job.holdUntil >= limited.until) this.holdThrough(job, limited.until);
        else this.finish(job, { ok: false, error: { kind: 'limited', limit: limited } });
        continue;
      }
      if (!this.canStart(job)) {
        i++;
        continue;
      }
      if (probe) this.lastWord.set(job.resource, now);
      this.queue.splice(i, 1);
      void this.start(job);
    }
    this.heldChanged();
  }

  /** Whether `job` may go through a primary limit to ask GitHub again: interactive, and none asked for `probeMs`. */
  private mayProbe(job: Job, now: number): boolean {
    if (job.rank !== RANK.interactive || !isBudgeted(job.resource)) return false;
    // Never through a secondary limit, or Changeling's own: those pause every resource.
    const all = this.pauses.get('all');
    if (all && all.until > now) return false;
    return now - (this.lastWord.get(job.resource) ?? 0) >= (this.opts.probeMs ?? 30_000);
  }

  /** Keep `job` out of the queue until `until`, then queue it again. */
  private holdThrough(job: Job, until: number) {
    job.state = 'held';
    job.heldTill = until;
    this.active.add(job);
    job.timer = setTimeout(() => {
      job.timer = null;
      if (job.state !== 'held') return;
      job.state = 'queued';
      job.heldTill = null;
      this.active.delete(job);
      this.queue.push(job);
      this.pump();
    }, Math.max(0, until - Date.now()));
  }

  /**
   * The primary limit `job` would hit, or null: its resource's known
   * budget has less left than it costs. Only `job` is refused; nothing is
   * paused on an estimate.
   */
  private overdraws(job: Job): RateLimit | null {
    if (job.resource === 'changelist' || !job.cost) return null;
    const left = this.left(job.resource);
    if (left === null || left >= job.cost) return null;
    const b = this.budget(job.resource)!;
    const spent = b.remaining - left;
    return { kind: 'primary', until: b.resetAt, stated: true, budget: { ...b, remaining: left, used: b.used + spent } };
  }

  private spend(job: Job) {
    const kept = this.budgets.get(job.resource);
    if (kept) kept.spent += job.cost;
  }

  private async start(job: Job) {
    job.state = 'running';
    this.active.add(job);
    this.running++;
    this.spend(job);
    let r: { ok: true; value: unknown } | { ok: false; error: Error };
    try {
      r = { ok: true, value: await job.send(job.controller.signal) };
    } catch (e) {
      r = { ok: false, error: e instanceof Error ? e : new Error(String(e)) };
    }
    this.running--;
    this.active.delete(job);
    job.state = 'done';
    // Aborted: its callers were answered when they left; the answer is no one's.
    if (!job.controller.signal.aborted) {
      if (r.ok) {
        if (this.strikes) {
          this.strikes = 0;
          this.limitsChanged();
        }
        if (this.window < this.opts.concurrency) this.window++;
        this.finish(job, r);
      } else this.failed(job, r.error);
    }
    this.pump();
  }

  private failed(job: Job, error: Error) {
    if (error instanceof RateLimitError) {
      // Already counted (noted on the way up): refuse with the pause in force, count nothing again.
      const l = error.noted ? (this.pausedFor(job.resource) ?? error.limit) : this.limitFrom(error.limit, job.resource);
      if (job.holdUntil >= l.until) this.holdThrough(job, l.until);
      else this.finish(job, { ok: false, error: { kind: 'limited', limit: l } });
      return;
    }
    const retries = this.opts.retries(error);
    if (job.idempotent && job.failures < retries) {
      const wait = backoffMs(job.failures, this.random);
      job.failures++;
      job.state = 'retrying';
      job.retryAt = Date.now() + wait;
      this.active.add(job);
      job.timer = setTimeout(() => {
        job.timer = null;
        if (job.state !== 'retrying') return;
        job.state = 'queued';
        job.retryAt = null;
        this.active.delete(job);
        this.queue.push(job);
        this.retryingChanged();
        this.pump();
      }, wait);
      this.retryingChanged();
      return;
    }
    this.finish(job, { ok: false, error: { kind: 'failed', error, attempts: job.failures + 1, retryable: retries > 0 } });
  }

  /** Pause for a limit an answer reported; returns the pause as kept (a secondary one backed off). */
  private limitFrom(l: RateLimit, resource: Resource): RateLimit {
    if (l.kind === 'primary') {
      const named = l.budget?.resource;
      return this.hold(l, named && isBudgeted(named) ? named : resource);
    }
    let out = l;
    if (l.kind === 'secondary') {
      this.window = 1;
      if (!l.stated) {
        const now = Date.now();
        const backoff = Math.min(MAX_SECONDARY_MS, (l.until - now) * 2 ** this.strikes);
        out = { ...l, until: Math.max(l.until, now + backoff) };
      }
      this.strikes++;
    }
    return this.hold(out, 'all');
  }
}

/** Whether `r` names one of GitHub's hourly budgets. */
function isBudgeted(r: string): r is 'graphql' | 'core' | 'search' {
  return r === 'graphql' || r === 'core' || r === 'search';
}

/** A separate copy of an answer for a caller who joined a shared request. */
function copy<T>(v: T): T {
  if (v === null || typeof v !== 'object') return v;
  try {
    return structuredClone(v);
  } catch {
    return v;
  }
}
